import { useEffect, useState } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { toast } from '@/hooks/use-toast';

export interface PushNotificationStatus {
  isSupported: boolean;
  isRegistered: boolean;
  token: string | null;
  permission: 'granted' | 'denied' | 'prompt' | 'unavailable';
}

export const usePushNotifications = () => {
  const [status, setStatus] = useState<PushNotificationStatus>({
    isSupported: false,
    isRegistered: false,
    token: null,
    permission: 'unavailable'
  });

  useEffect(() => {
    checkPushNotificationSupport();
    setupPushNotificationListeners();

    return () => {
      // Cleanup listeners
      PushNotifications.removeAllListeners();
    };
  }, []);

  const checkPushNotificationSupport = async () => {
    try {
      const permStatus = await PushNotifications.checkPermissions();
      setStatus(prev => ({
        ...prev,
        isSupported: true,
        permission: permStatus.receive as any
      }));

      // Si ya tiene permisos, registrar automáticamente
      if (permStatus.receive === 'granted') {
        await registerPushNotifications();
      }
    } catch (error) {
      console.log('Push notifications not supported on this platform:', error);
      setStatus(prev => ({
        ...prev,
        isSupported: false,
        permission: 'unavailable'
      }));
    }
  };

  const setupPushNotificationListeners = async () => {
    try {
      // Listener para cuando se registra exitosamente
      await PushNotifications.addListener('registration', (token) => {
        console.log('Push registration success, token:', token.value);
        setStatus(prev => ({
          ...prev,
          isRegistered: true,
          token: token.value,
          permission: 'granted'
        }));

        // Guardar el token en localStorage o enviarlo al backend
        localStorage.setItem('pushNotificationToken', token.value);

        toast({
          title: "Notificaciones Push Habilitadas",
          description: "Recibirás notificaciones importantes en tiempo real.",
        });
      });

      // Listener para errores de registro
      await PushNotifications.addListener('registrationError', (error) => {
        console.error('Error on registration:', error);
        setStatus(prev => ({
          ...prev,
          isRegistered: false,
          permission: 'denied'
        }));

        toast({
          title: "Error al habilitar notificaciones",
          description: "No se pudieron habilitar las notificaciones push.",
          variant: "destructive"
        });
      });

      // Listener para cuando llega una notificación
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push notification received:', notification);
        
        toast({
          title: notification.title || 'Nueva notificación',
          description: notification.body || '',
        });
      });

      // Listener para cuando el usuario toca la notificación
      await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('Push notification action performed:', notification);
        
        // Aquí puedes navegar a una pantalla específica basada en la notificación
        const data = notification.notification.data;
        if (data?.route) {
          // Navegar a la ruta específica
          window.location.href = data.route;
        }
      });
    } catch (error) {
      console.error('Error setting up push notification listeners:', error);
    }
  };

  const requestPermissions = async () => {
    try {
      const permStatus = await PushNotifications.requestPermissions();
      
      if (permStatus.receive === 'granted') {
        await registerPushNotifications();
      } else {
        setStatus(prev => ({
          ...prev,
          permission: 'denied'
        }));

        toast({
          title: "Permisos denegados",
          description: "Por favor, habilita las notificaciones en la configuración de tu dispositivo.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error requesting permissions:', error);
      toast({
        title: "Error",
        description: "No se pudieron solicitar los permisos de notificación.",
        variant: "destructive"
      });
    }
  };

  const registerPushNotifications = async () => {
    try {
      await PushNotifications.register();
      console.log('Registering for push notifications...');
    } catch (error) {
      console.error('Error registering push notifications:', error);
      toast({
        title: "Error",
        description: "No se pudo completar el registro de notificaciones.",
        variant: "destructive"
      });
    }
  };

  const unregisterPushNotifications = async () => {
    try {
      await PushNotifications.removeAllListeners();
      localStorage.removeItem('pushNotificationToken');
      
      setStatus({
        isSupported: true,
        isRegistered: false,
        token: null,
        permission: 'prompt'
      });

      toast({
        title: "Notificaciones deshabilitadas",
        description: "Ya no recibirás notificaciones push.",
      });
    } catch (error) {
      console.error('Error unregistering push notifications:', error);
    }
  };

  return {
    status,
    requestPermissions,
    unregisterPushNotifications
  };
};
